import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiUrl } from '@/lib/apiUrl';

const WsContext = createContext(null);

export function useWs() {
  return useContext(WsContext);
}

export function WsProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const reconnectTimer = useRef(null);
  const retryCount = useRef(0);
  const maxRetries = 3;

  const addNotification = useCallback((notif) => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [{ ...notif, id, timestamp: Date.now() }, ...prev].slice(0, 50));
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 8000);
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const connect = useCallback(() => {
    if (!user || wsRef.current?.readyState === WebSocket.OPEN) return;
    if (retryCount.current >= maxRetries) return; // Stop retrying after max attempts

    const backendUrl = getApiUrl();
    const wsUrl = backendUrl.replace(/^https?/, m => m === 'https' ? 'wss' : 'ws') + '/ws';
    const token = localStorage.getItem('token');
    if (!token) return; // no bearer token yet — nothing to authenticate the socket with

    try {
      // Auth via subprotocol: offer a marker + the JWT. The server reads the
      // JWT from the 2nd subprotocol and echoes only the marker, so the token
      // never appears in the URL (→ never in access logs) and works
      // cross-origin (the app uses Bearer tokens, not cookies).
      const ws = new WebSocket(wsUrl, ['nsofar-ws-v1', token]);

      ws.onopen = () => {
        setConnected(true);
        retryCount.current = 0; // Reset retry count on successful connection
      };

      ws.onclose = () => {
        setConnected(false);
        retryCount.current += 1;
        if (retryCount.current < maxRetries) {
          // Exponential backoff: 5s, 15s, 45s
          const delay = 5000 * Math.pow(3, retryCount.current - 1);
          reconnectTimer.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        // Don't log — just let onclose handle retry
        ws.close();
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          addNotification({ type: msg.type, message: msg.data?.message || '', data: msg.data });
        } catch (e) { /* ignore non-JSON */ }
      };

      wsRef.current = ws;
    } catch (e) {
      // WebSocket constructor can throw if URL is invalid
      retryCount.current = maxRetries; // Stop retrying
    }
  }, [user, addNotification]);

  useEffect(() => {
    retryCount.current = 0;
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  // Ping every 30s to keep alive
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <WsContext.Provider value={{ connected, notifications, addNotification, dismissNotification }}>
      {children}
    </WsContext.Provider>
  );
}
